import { useController } from "react-hook-form";
import { Info } from "lucide-react";

import { TextInputFieldProps } from "./types";
import { inputStyles } from "./inputStyles";

import { BasicTooltip } from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";

export const TextareaField = ({ field, form, stepId, size = "desktop" }: TextInputFieldProps) => {
  const { trigger, watch } = form;
  const fieldsInputs = watch(stepId);
  const isDisplayed = field.showIf ? field.showIf?.(fieldsInputs) : true;
  const key = `${stepId}.${field.id}`;
  const {
    field: { onChange, value, onBlur },
    fieldState: { error, isDirty, isTouched },
  } = useController({
    rules: {
      required: isDisplayed ? field.required : false,
      validate: val => (isDisplayed ? field.validation?.(val, fieldsInputs) : undefined),
    },
    control: form.control,
    name: key,
  });

  if (!isDisplayed) {
    return null;
  }

  const placeholder =
    typeof field.placeholder === "function" ? field.placeholder(fieldsInputs) : field.placeholder;
  const label = typeof field.label === "function" ? field.label(fieldsInputs) : field.label;

  return (
    <div className={cn(inputStyles.container({ size }))} key={field.id}>
      <div className="flex items-center gap-2">
        <label className={inputStyles.label({ size })}>{label}</label>
        {field.tooltip && (
          <BasicTooltip content={field.tooltip}>
            <Info className="h-4 w-4 cursor-pointer text-gray-500 hover:text-gray-700" />
          </BasicTooltip>
        )}
      </div>
      <textarea
        value={value ?? ""}
        onChange={e => {
          onChange(e.target.value);
          if (isDirty || isTouched) {
            trigger(key);
          }
        }}
        onBlur={() => {
          onBlur();
          trigger(key);
        }}
        readOnly={field.readOnly}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        rows={5}
        placeholder={placeholder}
        className={cn(
          "w-full resize-y rounded-lg border border-gray-200 bg-white px-3 py-2 font-mono text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500",
          size === "desktop" ? "min-h-[120px]" : "min-h-[80px]",
        )}
      />
      {error && (isDirty || isTouched) && <p className="text-sm text-red-500">{error.message}</p>}
    </div>
  );
};
